import { useEffect, useMemo } from 'react'
import { aMapCurrentCount, aSelectedListing, useAtomValue } from '~/atoms'
import { Button } from '~/components/ui'
import type { MapOutletContextData } from '~/hooks/map'
import { cn, debounce } from '~/lib/utils'
import MapListingCard from './listing-card'
import MapListingCardSkeleton from './listing-card-skeleton'

type MapCardListProps = MapOutletContextData & {
  className?: string
}

export default function MapCardList({
  className,
  listings,
  isLoading,
  isFetching,
  fetchNextPage,
  hasNextPage,
}: MapCardListProps) {
  const currentMapCount = useAtomValue(aMapCurrentCount)
  const selectedListing = useAtomValue(aSelectedListing)

  useEffect(() => {
    const elem = document.getElementById('cards-list')
    if (!fetchNextPage) {
      return
    }
    const onScroll = () => {
      if (
        hasNextPage &&
        !isFetching &&
        elem!.scrollTop > elem!.scrollHeight - elem!.scrollHeight / 4
      ) {
        fetchNextPage()
      }
    }
    const debouncedOnScroll = debounce(onScroll, 100)
    elem!.addEventListener('scroll', debouncedOnScroll)
    return () => elem!.removeEventListener('scroll', debouncedOnScroll)
  }, [hasNextPage, isFetching, fetchNextPage])

  const loadingCount = useMemo(() => {
    if (currentMapCount === 0 || (selectedListing && currentMapCount === 1)) {
      return 0
    }
    return 8
  }, [currentMapCount, selectedListing])

  return (
    <div className={cn(className, 'hidden lg:flex')}>
      <div
        id="cards-list"
        className={cn(
          'z-20 grid place-content-start gap-4 overflow-auto pb-4',
          '2xl:grid-cols-2',
          'md:grid-cols-1',
          'ml-4'
        )}>
        {isLoading && listings.length === 0
          ? Array.from({ length: loadingCount }, (_, index) => index).map(
              (index) => {
                return (
                  <MapListingCardSkeleton
                    key={index}
                    className={cn(
                      'md:w-[350px] 2xl:w-[350px]',
                      loadingCount < 4 && 'col-span-2'
                    )}
                  />
                )
              }
            )
          : listings.map((listing, index) => {
              return (
                <MapListingCard
                  key={index}
                  className={cn(
                    isFetching && 'animate-pulse',
                    'md:w-[350px] 2xl:w-[350px]',
                    loadingCount < 4 && 'col-span-2'
                  )}
                  listing={listing}
                />
              )
            })}
        {hasNextPage && fetchNextPage && (
          <div className="col-span-full flex justify-center">
            <Button disabled={isFetching} onClick={() => fetchNextPage()}>
              {isFetching ? 'Loading' : 'Load More'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
